/* global L */

import { createTileLayerComponent, updateGridLayer } from "@react-leaflet/core";
import isObject from "lodash/isObject";
import isFunction from "lodash/isFunction";
import isString from "lodash/isString";
import isEmpty from "lodash/isEmpty";
import cloneDeep from "lodash/cloneDeep";
import extend from "lodash/extend";
import find from "lodash/find";
import isEqual from "lodash/isEqual";

// Load the customized Leaflet.VectorGrid library
import "../../lib/Leaflet.VectorGrid";

L.DomEvent.fakeStop = L.DomEvent.stop;

const patchVectorGridLayer = obj => {
  // Save the original _createLayer method
  obj._createLayer_orig = obj._createLayer;
  // Override the _createLayer method
  obj._createLayer = function (feat, pxPerExtent, layerStyle) {
    let layer = this._createLayer_orig(feat, pxPerExtent, layerStyle);
    // Set layer.getLatLng to null to prevent errors
    if (feat.type === 1) layer.getLatLng = null;

    return layer;
  };

  return obj;
};

// Redrawing an icon replaces its `<image>` node. Leaflet.VectorGrid only registers the
// original node for pointer events, so the replacement must be rewired manually.
// Unregister the previous node first to avoid keeping detached targets in the map.
const rewireRedrawnIcon = (vectorGrid, layer, previousPath) => {
  const map = vectorGrid._map;
  const path = layer._path;

  if (!map || !vectorGrid.options.interactive || path === previousPath) return;

  if (previousPath) delete map._targets[L.stamp(previousPath)];

  path.style.pointerEvents = "auto";
  map._targets[L.stamp(path)] = layer;
};

// This function allows to update the styles of the vector grid when the component's
// `vectorTileLayerStyles` prop is updated.
//
// It looks for the featureIds stored in each loaded tile, and updates the current loaded
// feature styles with the styles associated to their `layerName`.
const updateVectorGridStyles = (vectorGrid, props) => {
  const { vectorTileLayerStyles: styles } = props;

  // Update each feature style once (avoiding to call `setFeatureStyle` multiple times for
  // the same feature in different tiles).
  Object.values(vectorGrid._vectorTiles).reduce((features, tile) => {
    const processedFeatures = { ...features };

    // Update the vector grid instance options to ensure that tiles renderer after the
    // update use the new styles.
    vectorGrid.options.vectorTileLayerStyles = styles;

    Object.entries(tile._features).forEach(([featureId, { layerName }]) => {
      if (processedFeatures[featureId]) return;
      processedFeatures[featureId] = true;

      vectorGrid.setFeatureStyle(featureId, styles[layerName]);
    });

    Object.values(tile._layers).forEach(layer => {
      if (!layer._getImage) return;

      const previousPath = layer._path;

      layer._updatePath();
      rewireRedrawnIcon(vectorGrid, layer, previousPath);
    });

    return processedFeatures;
  }, {});
};

const createTileLayer = (props, context) => {
  let highlight;
  let active;

  const {
    data,
    style,
    hoverStyle,
    activeStyle,
    onClick,
    onMouseover,
    onMouseout,
    onDblclick,
    onContextmenu,
    vectorTileLayerStyles,
    url,
    accessKey,
    accessToken,
    type = "protobuf",
    interactive = true,
    idField,
    popup,
    ...rest
  } = props;
  delete rest.leaflet;

  const baseStyle = (properties, _zoom) => {
    if (isFunction(style)) {
      return style(properties);
    } else if (isObject(style)) {
      return style;
    }

    return {
      weight: 0.5,
      opacity: 1,
      color: "#ccc",
      fillColor: "#390870",
      fillOpacity: 0.6,
      fill: true,
      stroke: true
    };
  };

  const setFeatureStyle = (id, style) => {
    vectorGrid.setFeatureStyle(id, style);
  };

  const resetFeatureStyle = id => {
    vectorGrid.resetFeatureStyle(id);
  };

  const clearHighlight = properties => {
    if (highlight) {
      if (highlight !== active) {
        resetFeatureStyle(highlight);
      } else {
        let st;

        if (isFunction(activeStyle)) {
          st = activeStyle(properties);
        } else if (isObject(activeStyle)) {
          st = cloneDeep(activeStyle);
        }

        if (!isEmpty(st)) {
          const base = cloneDeep(baseStyle(properties));
          const activeStyle = extend(base, st);
          setFeatureStyle(active, activeStyle);
        }
      }
    }
    highlight = null;
  };

  const clearActive = () => {
    if (active) {
      resetFeatureStyle(active);
    }
    active = null;
  };

  const getFeature = featureId => {
    if (isEmpty(data) || isEmpty(data.features)) return {};
    const feature = find(
      data.features,
      ({ properties }) => properties[idField] === featureId
    );
    return cloneDeep(feature);
  };

  const _getFeatureId = ({ idField }, feature) => {
    if (isFunction(idField)) {
      return idField(feature);
    } else if (isString(idField)) {
      return feature.properties[idField];
    }
  };

  const _propagateEvent = (eventHandler, e) => {
    if (!isFunction(eventHandler)) return;
    const featureId = _getFeatureId(e.layer);
    const feature = getFeature(featureId);
    // Originally, the code used `cloneDeep(e)` to build the event object, but it created
    // infinite loops in certain situations.
    //
    // Note that the feature can only be retrieved for the "slicer" VectorGrid mode, as
    // there's no `data` prop passed to the component when provided the "protobuf" type
    // mode.
    const event = { ...e, feature };

    eventHandler(event);
  };

  const handleMouseOver = e => {
    const { properties } = e.layer;
    if (!properties) return; // Add check to avoid undefined properties

    _propagateEvent(onMouseover, e);

    let st;
    const featureId = _getFeatureId(e.layer);

    if (isFunction(hoverStyle)) {
      st = hoverStyle(properties);
    } else if (isObject(hoverStyle)) {
      st = cloneDeep(hoverStyle);
    }

    if (!isEmpty(st) && featureId) {
      clearHighlight(properties);
      highlight = featureId;
      const base = cloneDeep(baseStyle(properties));
      const hoverStyle = extend(base, st);
      setFeatureStyle(featureId, hoverStyle);
    }
  };

  const handleMouseOut = e => {
    const { properties } = e.layer;
    if (!properties) return; // Add check to avoid undefined properties

    _propagateEvent(onMouseout, e);
    clearHighlight(properties);
  };

  const handleClick = e => {
    const { properties } = e.layer;

    if (!properties) return;

    const featureId = _getFeatureId(e.layer);

    _propagateEvent(onClick, e);

    let st;
    if (isFunction(activeStyle)) {
      st = activeStyle(properties);
    } else if (isObject(activeStyle)) {
      st = cloneDeep(activeStyle);
    }

    if (!isEmpty(st) && featureId) {
      clearActive();
      active = featureId;
      const base = cloneDeep(baseStyle(properties));
      const activeStyle = extend(base, st);
      setFeatureStyle(featureId, activeStyle);
    }
  };

  const handleDoubleClick = e => {
    _propagateEvent(onDblclick, e);
    clearActive();
  };

  const handleContextMenu = e => {
    _propagateEvent(onContextmenu, e);
    clearActive();
  };

  const vectorGridCommonProps = {
    interactive,
    getFeatureId: feature => _getFeatureId(props, feature),
    rendererFactory: L.svg.tile,
    ...rest
  };

  const buildVectorGrid = () => {
    if (type === "slicer") {
      return new L.vectorGrid.slicer(data, {
        vectorTileLayerStyles: vectorTileLayerStyles || {
          sliced: (properties, zoom) => {
            const bs = baseStyle(properties, zoom);
            bs.fill = true;
            bs.stroke = true;
            return bs;
          }
        },
        ...vectorGridCommonProps
      });
    } else {
      return new L.vectorGrid.protobuf(url, {
        key: accessKey,
        token: accessToken,
        vectorTileLayerStyles,
        ...vectorGridCommonProps
      });
    }
  };

  const vectorGrid = patchVectorGridLayer(buildVectorGrid());

  vectorGrid
    .on("mouseover", handleMouseOver)
    .on("mouseout", handleMouseOut)
    .on("click", handleClick)
    .on("dblclick", handleDoubleClick)
    .on("contextmenu", handleContextMenu);

  if (popup) {
    vectorGrid.bindPopup(popup);
  }

  return {
    instance: vectorGrid,
    context
  };
};

const updateTileLayer = (vectorGrid, props, prevProps) => {
  updateGridLayer(vectorGrid, props, prevProps);

  // If the `vectorTileLayerStyles` prop really changed, update the vector grid styles to
  // reflect the changes
  if (!isEqual(props.vectorTileLayerStyles, prevProps.vectorTileLayerStyles)) {
    updateVectorGridStyles(vectorGrid, props);
  }
};

const VectorGrid = createTileLayerComponent(createTileLayer, updateTileLayer);

export default VectorGrid;
